import React from "react";
import styled, { CSSProperties } from "styled-components";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { FlowEditContext } from "../../FlowEditContext";
import { FlowStepData } from "../../../rule-engine-core/Flow/Steps/FlowStepData";
import { FlowStepSequence } from "../../../rule-engine-core/Flow/Structure/FlowStepSequence";
import { FlowConfiguration } from "../../../rule-engine-core/Flow/FlowConfiguration";
import { RenderCollapsedStack } from "./RenderCollapsedStack";
import { RenderStepHost } from "./RenderStepHost";
import { Box } from "@mui/material";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { ColorUtilities } from "../../../rule-engine-core/Common/ColorUtilities";

const FlowSequenceContent = styled.div<{ height?: number }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: ${(props) => (props.height ? `${props.height}px` : "auto")};
`;

const FlowSequenceEmptyContent = styled.div<{ styles: CSSProperties }>`
    display: flex;
    margin: -4px -8px -2px;
    border: 1px dashed ${UIStyleConstants.GRAY_LIGHTER_COLOR};
    border-radius: 4px;
    align-items: center;
    justify-content: center;
    font-size: ${(props) => props.styles.fontSize || 16}px;
    line-height: 20px;
    color: ${(props) => props.styles.color};
    flex: 1;
    font-weight: 600;
`;

interface IRegularSequenceContent {
    sequence: FlowStepSequence;
    editContext: FlowEditContext;
    flow: FlowConfiguration;
    parentColor: string;
}

export const RenderSequenceContent: React.FC<IRegularSequenceContent> = (props: IRegularSequenceContent) => {
    let sequence = props.sequence;
    let editContext = props.editContext;
    let flow = props.flow;
    let sequenceSize = editContext.getSequenceMainSize(sequence);

    if (sequence.isCollapsed)
        return (
            <RenderCollapsedStack
                sequence={sequence}
                editContext={editContext!}
                flow={flow!}
            />
        );
    else {
        return (
            <FlowSequenceContent height={sequenceSize.height}>
                {sequence.steps.length === 0 && !editContext.isDragOver(sequence._id) && (
                    <FlowSequenceEmptyContent
                        styles={{
                            fontSize: 16,
                            color: ColorUtilities.isLight(props.parentColor)
                                ? UIStyleConstants.TEXT_COLOR_DEFAULT
                                : UIStyleConstants.GRAY_VERY_LIGHT_COLOR,
                        }}
                    >
                        Put steps here
                    </FlowSequenceEmptyContent>
                )}
                {sequence.steps.map((step: FlowStepData, i: number) => {
                    return (
                        <ObserveState
                            listenTo={step}
                            key={step._id}
                            control={() => (
                                <RenderStepHost
                                    editContext={editContext!}
                                    sequence={sequence}
                                    index={i}
                                    step={step}
                                />
                            )}
                        />
                    );
                })}
            </FlowSequenceContent>
        );
    }
};

interface IBranchSequenceContent {
    sequence: FlowStepSequence;
    editContext: FlowEditContext;
    flow: FlowConfiguration;
    steps: FlowStepData[];
}

export const RenderBranchSequenceContent: React.FC<IBranchSequenceContent> = (props: IBranchSequenceContent) => {
    let sequence = props.sequence;
    let editContext = props.editContext;

    return (
        <Box
            display={"flex"}
            flexDirection={"row"}
            minHeight={"60px"}
        >
            {props.steps.length === 0 && !editContext.isDragOver(sequence._id) && (
                <FlowSequenceEmptyContent
                    styles={{
                        fontSize: 16,
                    }}
                >
                    Put steps here
                </FlowSequenceEmptyContent>
            )}
            {props.steps.map((step: FlowStepData, i: number) => {
                return (
                    <ObserveState
                        listenTo={step}
                        key={step._id}
                        control={() => (
                            <RenderStepHost
                                editContext={editContext!}
                                sequence={sequence}
                                index={i}
                                step={step}
                            />
                        )}
                    />
                );
            })}
        </Box>
    );
};
